import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import Service, { SuperExperiment, Variable, VariableInfo } from '@/lib/App';
import {
  AccessTarget,
  buildAccessTargetsFromVariables,
} from '@/utils/accessControl';
import { error } from '@/utils/logging';

interface AccessDataForExperiment {
  techFamilies: string[];
  accessTargets: AccessTarget[];
}

interface ExperimentContextType {
  isLoading: boolean;
  getTechFamiliesForExperiment: (
    experiment: SuperExperiment,
  ) => Promise<string[]>;
  getAccessTargetsForExperiment: (
    experiment: SuperExperiment,
  ) => Promise<AccessTarget[]>;
  getAccessDataForExperiment: (
    experiment: SuperExperiment,
  ) => Promise<AccessDataForExperiment>;
  getVariable: (name: string) => Promise<VariableInfo>;
}

const ExperimentContext = createContext<ExperimentContextType | undefined>(
  undefined,
);

export const ExperimentContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const variableCache = useRef<Record<string, VariableInfo>>({});
  const variableRequests = useRef<Record<string, Promise<VariableInfo | null>>>(
    {},
  );

  const fetchVariable = useCallback(
    async (name: string): Promise<VariableInfo | null> => {
      const variableName = name?.trim();

      if (!variableName) return null;

      if (variableCache.current[variableName]) {
        return variableCache.current[variableName];
      }

      if (!variableRequests.current[variableName]) {
        variableRequests.current[variableName] = Service.planner
          .fetchVariable(variableName)
          .then((variable) => {
            if (variable?.name) {
              variableCache.current[variable.name] = variable;
              variableCache.current[variableName] = variable;
            }

            return variable;
          })
          .catch((err) => {
            error(`Fetching variable ${variableName} failed: ${err}`);
            return null;
          })
          .finally(() => {
            delete variableRequests.current[variableName];
          });
      }

      return variableRequests.current[variableName];
    },
    [],
  );

  const getVariablesForExperiment = useCallback(
    async (experiment: SuperExperiment): Promise<VariableInfo[]> => {
      const variableNames = Array.from(
        new Set(
          (experiment?.variables ?? [])
            .map((variable: Variable | string) =>
              typeof variable === 'string' ? variable : variable?.name,
            )
            .filter(Boolean),
        ),
      );

      if (!variableNames.length) return [];

      const cachedVariableNames = variableNames.filter(
        (name) => variableCache.current[name],
      );

      if (cachedVariableNames.length === variableNames.length) {
        return variableNames
          .map((name) => variableCache.current[name])
          .filter((variable) => Boolean(variable));
      }

      setIsLoading(true);
      try {
        await Promise.all(
          variableNames
            .filter((name) => !variableCache.current[name])
            .map(fetchVariable),
        );
      } finally {
        setIsLoading(false);
      }

      return variableNames
        .map((name) => variableCache.current[name])
        .filter((variable) => Boolean(variable));
    },
    [fetchVariable],
  );

  const getTechFamiliesForExperiment = useCallback(
    async (experiment: SuperExperiment): Promise<string[]> => {
      const variables = await getVariablesForExperiment(experiment);
      return variables.map((variable) => variable.techFamily);
    },
    [getVariablesForExperiment],
  );

  const getAccessTargetsForExperiment = useCallback(
    async (experiment: SuperExperiment): Promise<AccessTarget[]> => {
      const variables = await getVariablesForExperiment(experiment);
      return buildAccessTargetsFromVariables(variables);
    },
    [getVariablesForExperiment],
  );

  const getAccessDataForExperiment = useCallback(
    async (experiment: SuperExperiment): Promise<AccessDataForExperiment> => {
      const variables = await getVariablesForExperiment(experiment);
      return {
        techFamilies: variables.map((variable) => variable.techFamily),
        accessTargets: buildAccessTargetsFromVariables(variables),
      };
    },
    [getVariablesForExperiment],
  );

  const getVariable = useCallback(
    async (name: string) => {
      return fetchVariable(name);
    },
    [fetchVariable],
  );

  const contextValue = useMemo(
    () => ({
      isLoading,
      getTechFamiliesForExperiment,
      getAccessTargetsForExperiment,
      getAccessDataForExperiment,
      getVariable,
    }),
    [
      getAccessDataForExperiment,
      getAccessTargetsForExperiment,
      getTechFamiliesForExperiment,
      getVariable,
      isLoading,
    ],
  );

  return (
    <ExperimentContext.Provider value={contextValue}>
      {children}
    </ExperimentContext.Provider>
  );
};

export const useExperimentContext = () => {
  const context = useContext(ExperimentContext);
  if (!context) {
    error(
      new Error(
        'useExperimentContext must be used within a ExperimentContextProvider',
      ),
    );
  }
  return context;
};
